/**
 * Выбор сервера в интерфейсе.
 *
 * Выбранный сервер живёт в cookie и читается сервером на каждом запросе,
 * поэтому переключение сделано полной перезагрузкой страницы: иначе уже
 * загруженные наборы, игроки и заявки остались бы от прежнего сообщества.
 */

import type { AccessLevel, SessionUser } from '@ak/shared';

import { apiPost } from './api.ts';

/** Сервер, доступный вошедшему: ответ `GET /api/guilds`. */
export interface GuildAccess {
  guildId: string;
  name: string;
  iconUrl: string | null;
  level: AccessLevel;
  /** Созданы ли роли, каналы и панели. */
  configured: boolean;
}

/** Сервер, выбранный для текущего запроса. */
export interface SelectedGuild {
  id: string;
  name: string;
  iconUrl: string | null;
  configured: boolean;
}

export interface CommunityBasics {
  timezone: string;
  eventTypes: string[];
  defaultCapacity: number;
}

export interface MeResponse {
  user: SessionUser | null;
  guild: SelectedGuild | null;
  settings: CommunityBasics | null;
  auth: { oauthConfigured: boolean; devLoginEnabled: boolean };
  bot: { ready: boolean; username: string | null; avatarUrl: string | null; inviteUrl: string };
}

/**
 * Какой экран показывать.
 *
 * Вынесено из компонента отдельной функцией: правил четыре, они завязаны
 * на состояние сервера, и проверять их проще без разметки.
 */
export type Shell =
  | { kind: 'login' }
  | { kind: 'picker'; user: SessionUser }
  | { kind: 'setup'; user: SessionUser; guild: SelectedGuild }
  | { kind: 'app'; user: SessionUser; guild: SelectedGuild; settings: CommunityBasics };

export function resolveShell(me: MeResponse): Shell {
  const user = me.user;
  if (user === null) {
    return { kind: 'login' };
  }

  // Сервер не выбран: их несколько, либо тот, что в cookie, стал недоступен.
  // Настройки без сервера не имеют смысла, поэтому приходят пустыми.
  const guild = me.guild;
  if (guild === null || me.settings === null) {
    return { kind: 'picker', user };
  }

  if (!guild.configured) {
    return { kind: 'setup', user, guild };
  }

  return { kind: 'app', user, guild, settings: me.settings };
}

/**
 * Запоминает сервер на стороне сервера и перезагружает приложение.
 *
 * Возвращает на тот же адрес, а не на корень: по кнопке из Discord человек
 * приходит сразу на статистику, и корень после выбора сервера увёл бы его
 * оттуда — в «Обзор», который игроку закрыт.
 */
export async function selectGuild(guildId: string, returnTo = window.location.pathname): Promise<void> {
  await apiPost('/api/guilds/select', { guildId });
  window.location.assign(returnTo);
}
